import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, DollarSign, Gauge, TrendingDown, TriangleAlert } from 'lucide-react';
import { SinDatos, DataState, Panel, PanelHeader } from '@/components/ui';
import { KpiCard } from '@/components/domain/KpiCard';
import { PageHeader } from '@/components/layout/PageHeader';
import { RecommendationCard } from '@/components/domain/RecommendationCard';
import { FillRateChart } from '@/components/charts/FillRateChart';
import { UpdateDataPanel } from './UpdateDataPanel';
import { useDashboard, useForecast, useRecommendations } from '@/lib/hooks';
import { formatCompactCop } from '@shared/format';
import { TONE_TEXT, type Tone } from '@/lib/status';
import { cn } from '@/lib/cn';
import type { Kpi } from '@shared/types';

const KPI_META: Record<string, { icon: typeof DollarSign; tone: Tone; invert?: boolean }> = {
  inventory_value: { icon: DollarSign, tone: 'primary' },
  coverage: { icon: CalendarDays, tone: 'info' },
  fill_rate: { icon: Gauge, tone: 'warning' },
  lost_sales: { icon: TrendingDown, tone: 'danger', invert: true },
};

export default function DashboardPage() {
  const dash = useDashboard();
  const forecast = useForecast();
  const recs = useRecommendations();

  return (
    <DataState query={dash}>
      {(d) => {
      const totalRecs =
        d.recommendationBreakdown.critico + d.recommendationBreakdown.alto + d.recommendationBreakdown.medio;
      return (
        <div className="space-y-6">
          {/* Era la única pantalla sin <h1>: se apoyaba en el título que repetía la barra
              superior, que se sacó por duplicado. Un documento sin encabezado propio deja
              al lector de pantalla sin punto de entrada. */}
          <PageHeader
            title="Dashboard"
            subtitle="El estado del inventario y qué hay que comprar, de un vistazo"
          />

          {/* De cuándo son los datos y el botón para traerlos. Va primero porque condiciona
              la lectura de todo lo que sigue. */}
          <UpdateDataPanel />

          {/* Los KPIs se calculan de los datos reales (ver routes/dashboard.ts). Antes salían
              de `kpi_snapshots`, que era del seed y que la corrida del motor no escribe: la
              tarjeta decía "Próximamente · Fase 2" para siempre. Si la lista viene vacía es
              porque todavía no se ingirió nada, y eso se dice con esas palabras. */}
          {d.kpis.length === 0 ? (
            <div className="surface p-5 text-xs leading-relaxed text-ink-faint">
              Todavía no hay datos ingeridos, así que no hay nada que resumir. Apretá{' '}
              <strong className="text-ink-strong">Actualizar datos</strong> arriba.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {d.kpis.map((kpi: Kpi) => {
                const meta = KPI_META[kpi.key] ?? { icon: DollarSign, tone: 'primary' as Tone };
                return <KpiCard key={kpi.key} kpi={kpi} icon={meta.icon} tone={meta.tone} invert={meta.invert} />;
              })}
            </div>
          )}

          {/* Breakdown row */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel className="p-4">
              <Header title="Estado de SKUs" to="/inventario" cta="Ver semáforo" />
              <div className="mt-3 grid grid-cols-5 gap-2">
                <Mini n={d.inventoryBreakdown.critico} label="Críticos" tone="danger" to="/inventario?estado=critico" />
                <Mini n={d.inventoryBreakdown.alerta} label="Alertas" tone="warning" to="/inventario?estado=alerta" />
                <Mini n={d.inventoryBreakdown.saludable} label="OK" tone="success" to="/inventario?estado=saludable" />
                <Mini n={d.inventoryBreakdown.sobrestock} label="Sobrestock" tone="violet" to="/inventario?estado=sobrestock" />
                <Mini n={d.inventoryBreakdown.sin_datos} label="Sin datos" tone="neutral" to="/inventario?estado=sin_datos" />
              </div>
              {d.inventoryBreakdown.sin_datos > 0 && (
                <p className="mt-3 text-2xs leading-relaxed text-ink-faint">
                  {d.inventoryBreakdown.sin_datos} SKU no registran venta en el B2B en el período
                  ingerido: no se les puede calcular cobertura. No son stock saludable.
                </p>
              )}
            </Panel>

            <Panel className="p-4">
              <Header title="Recomendaciones IA" to="/recomendaciones" cta="Ver todas" />
              {totalRecs === 0 ? (
                <p className="mt-4 text-xs leading-relaxed text-ink-faint">
                  Ningún producto llegó a su punto de reorden todavía. Las recomendaciones las
                  calcula el motor con cada corrida.
                </p>
              ) : (
                <>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Mini n={d.recommendationBreakdown.critico} label="Críticas" tone="danger" to="/recomendaciones?urgencia=critico" />
                    <Mini n={d.recommendationBreakdown.alto} label="Altas" tone="warning" to="/recomendaciones?urgencia=alto" />
                    <Mini n={d.recommendationBreakdown.medio} label="Medias" tone="info" to="/recomendaciones?urgencia=medio" />
                  </div>
                  {/* El capital era una línea de texto chico al pie, y es el número con el
                      que se decide: acá arriba pesa lo que tiene que pesar. */}
                  <div className="mt-4 border-t border-hairline pt-3">
                    <p className="text-2xs uppercase tracking-wide text-ink-faint">Capital requerido</p>
                    {/* Con costos sin cargar este total es incompleto y se lee como completo:
                        en producción daba $16 K COP para reabastecer 61 productos. */}
                    {forecast.data?.resumen && forecast.data.resumen.sinCosto > 0 ? (
                      <p className="mt-0.5 text-sm font-semibold text-warning-text">
                        sin costos cargados
                      </p>
                    ) : (
                      <p className="cifra mt-0.5 text-2xl">
                        {formatCompactCop(d.recommendationBreakdown.capital)}{' '}
                        <span className="text-sm font-normal text-ink-muted">COP</span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </Panel>

            {/* ⚠️ Acá estaba «Precisión del Modelo», con el error medido del motor. Se sacó
                por decisión de Charly (2026-09-01): **el número no es lo bastante confiable
                para ponerlo delante de un usuario**. Se calcula igual y queda guardado — nos
                sirve a nosotros para saber si el motor mejora o empeora—, pero un porcentaje
                de error en pantalla se lee como una garantía sobre lo que va a pasar, y no lo
                es. El panel de la proyección global, que sí sale de datos, ocupa su lugar. */}
          </div>

          {/* Alert banner — solo si hay alertas. Con cero anunciaba en rojo "0 alertas activas". */}
          {d.alertSummary.active > 0 && (
          <div className="surface border-l-2 border-l-danger p-4">
            <div className="flex items-start gap-3">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger-text" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-ink-strong">
                    {d.alertSummary.active} alertas activas — {d.alertSummary.immediate} requieren acción inmediata
                  </p>
                  <Link to="/alertas" className="shrink-0 text-xs font-medium text-primary-text hover:underline">
                    Ver todas
                  </Link>
                </div>
                <ul className="mt-2 space-y-1">
                  {d.alertSummary.highlights.map((h: string) => (
                    <li key={h} className="flex items-start gap-2 text-xs text-ink-muted">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-danger" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          )}

          {/* Este bloque decía "falta el motor que proyecta" y era un cartel de "Fase 2" ESCRITO FIJO,
              no condicionado a nada: siguió diciéndolo después de que el motor existiera, y el
              gráfico completo ya estaba andando en /forecast. Es la misma familia que el
              subtítulo "SES · Holt · Croston" que sobrevivió al cambio que lo desmentía.
              Ahora muestra el resumen REAL y manda a la pantalla que tiene el detalle; si
              todavía no se calculó, lo dice con esas palabras en vez de culpar al motor. */}
          <Panel>
            <PanelHeader title="Proyección de Demanda Global" subtitle="Lo que el motor proyecta para todo el catálogo" />
            <div className="p-5">
              {forecast.data?.resumen == null || forecast.data.resumen.conProyeccion === 0 ? (
                <p className="text-xs leading-relaxed text-ink-faint">
                  Todavía no se calculó el forecast. Apretá <strong>Actualizar datos</strong> arriba:
                  trae la venta del B2B y corre el motor.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-2xl font-bold text-ink-strong">
                      {Math.round(forecast.data.resumen.demandaDiariaTotal).toLocaleString('es-CO')}
                    </p>
                    <p className="text-xs text-ink-muted">unidades por día, todo el catálogo</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink-strong">
                      {forecast.data.resumen.aComprar.toLocaleString('es-CO')}
                    </p>
                    <p className="text-xs text-ink-muted">productos en punto de reorden</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-ink-strong">
                      {forecast.data.resumen.sinCosto > 0
                        ? 'sin costos'
                        : formatCompactCop(forecast.data.resumen.capital)}
                    </p>
                    <p className="text-xs text-ink-muted">capital estimado para cubrirlos</p>
                  </div>
                  <div className="sm:col-span-3">
                    <Link to="/forecast" className="text-xs font-semibold text-primary-text hover:underline">
                      Ver la proyección y el detalle por producto →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </Panel>

          {/* Fill rate — la serie mensual era seed; la real se acumula corrida a corrida. */}
          <Panel>
            <PanelHeader title="Fill Rate Mensual" subtitle="Cuánto de lo pedido se pudo entregar" />
            <div className="p-5">
              {d.fillRate.length === 0 ? (
                <SinDatos
                  title="Todavía no hay serie mensual"
                  detail="Se calcula cruzando lo vendido con lo que no se pudo entregar, mes a mes. Con pocos meses la línea no dice nada: se vuelve útil a medida que se acumula."
                  comoLlenarlo="Se llena sola con cada corrida. El fill rate del mes en curso ya está arriba, en los KPIs."
                />
              ) : (
                <FillRateChart data={d.fillRate} />
              )}
            </div>
          </Panel>

          {/* Urgent recommendations — el "(5)" del link estaba hardcodeado. */}
          {totalRecs > 0 && (
            <Panel>
              <PanelHeader
                title="Recomendaciones Urgentes"
                right={
                  <Link to="/recomendaciones" className="inline-flex items-center gap-1 text-xs font-medium text-primary-text hover:underline">
                    Ver todas ({totalRecs}) <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                }
              />
              <div className="space-y-4 p-5">
                <DataState query={recs}>
                  {(r) =>
                    r.items
                      .filter((rec) => rec.severity === 'critico')
                      .slice(0, 2)
                      .map((rec) => <RecommendationCard key={rec.id} rec={rec} />)
                  }
                </DataState>
              </div>
            </Panel>
          )}
        </div>
      );
      }}
    </DataState>
  );
}

function Header({ title, to, cta }: { title: string; to?: string; cta?: string }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold text-ink-strong">{title}</h3>
      {to && cta && (
        <Link to={to} className="inline-flex items-center gap-1 text-xs font-medium text-primary-text hover:underline">
          {cta} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}

/** Una cifra del desglose. Con `to` es un enlace al listado ya filtrado — que es lo que
 *  uno quiere hacer apenas ve el número, y antes obligaba a ir y filtrar de nuevo a mano. */
function Mini({ n, label, tone, to }: { n: number; label: string; tone: Tone; to?: string }) {
  const cuerpo = (
    <>
      <p className={cn('text-xl font-bold', TONE_TEXT[tone])}>{n}</p>
      <p className="mt-0.5 text-2xs text-ink-muted">{label}</p>
    </>
  );
  const clases = 'block rounded-lg border border-hairline bg-subtle p-2 text-center';
  if (!to || n === 0) {
    // Con cero no hay nada que abrir: un enlace a una lista vacía es una promesa incumplida.
    return <div className={clases}>{cuerpo}</div>;
  }
  return (
    <Link
      to={to}
      className={cn(
        clases,
        'transition-[background-color,border-color,scale] hover:border-primary/40 hover:bg-hover active:scale-[0.96]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring',
      )}
    >
      {cuerpo}
    </Link>
  );
}

